import React, { useState, useEffect, useContext } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { Auth } from '../apis/secure/auth';
import { AuthContext } from '../reducers/Auth/AuthContext';
import { WSContext } from '../contexts/WSContext';
import BarLoader from 'react-spinners/BarLoader';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(100vh - 100px)',
  },
  form: {
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'Roboto !important',
  },
  submit: {
    width: '120px',
  },
}));

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

const LoginPage = () => {
  const [email, setEmail] = useState({ value: '', error: false, message: '' });
  const [pass, setPass] = useState({ value: '', error: false, message: '' });
  const [error, setError] = useState(null);
  const [loginAccessTokenTry, setLoginAccessTokenTry] = useState(true);
  const [loginTry, setLoginTry] = useState(false);
  const [authState, authDispatch] = useContext(AuthContext);
  const { WSConnection, setWSConnection } = useContext(WSContext);
  const classes = useStyles();
  const history = useHistory();
  let location = useLocation();
  const { from } = location.state || { from: { pathname: '/live' } };

  const routeChange = () => {
    const fromLogin = !!from.pathname && (from.pathname === '/' || from.pathname === '/login');
    const path = fromLogin ? '/live' : from.pathname === undefined ? '/live' : from.pathname;
    history.push(path);
  };

  const authenticateAndGoLive = (user) => {
    const { userId, alias, role, placeId } = user;
    if (!userId || !alias || !role || !placeId) {
      console.error('DATA NOT FOUND IN TOKEN PARSE');
      return;
    }
    authDispatch({ type: 'LOGIN', payload: { user } });
  };

  useEffect(() => {
    if (WSConnection) {
      setWSConnection(null);
    }

    const checkCredentials = async () => {
      try {
        setLoginAccessTokenTry(true);
        const { success, user, message } = await Auth.loginWithRefreshToken();
        if (!success) {
          setLoginAccessTokenTry(false);
          setError(message);
          authDispatch({ type: 'LOGOUT', payload: {} });
        } else {
          // authDispatch({ type: 'SET_LOADING', payload: { loading: true } });
          setLoginAccessTokenTry(false);
          authenticateAndGoLive(user);
        }
      } catch (error) {
        console.log('ERROR ON LOGIN', error);
        authDispatch({ type: 'LOGOUT', payload: {} });
        setLoginAccessTokenTry(false);
      }
    };
    checkCredentials();
  }, []);

  useEffect(() => {
    if (authState.user.userId && authState.user.placeId) {
      routeChange();
    }
  }, [authState]);

  const handleEmail = (e) => {
    setEmail({
      value: `${e.target.value}`.toLowerCase(),
      error: false,
    });
  };

  const handlePassword = (e) => {
    setPass({
      value: `${e.target.value}`,
      error: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginTry(true);
    const emailIsValid = validateEmail(email.value);
    const passIsValid = pass.value.length > 3 && pass.value.length <= 64;
    if (!emailIsValid || !passIsValid) {
      setEmail({
        value: email.value,
        error: !emailIsValid,
      });

      setPass({
        value: pass.value,
        error: !passIsValid,
      });
      setLoginTry(false);
      return;
    }
    authDispatch({ type: 'SET_LOADING', payload: { loading: true } });
    const { success, user, error } = await Auth.login(email.value, pass.value);

    if (success) {
      authenticateAndGoLive(user);
      authDispatch({ type: 'SET_LOADING', payload: { loading: false } });
    } else {
      setError(error);
      authDispatch({ type: 'SET_LOADING', payload: { loading: false } });
      setLoginTry(false);
    }
  };

  if (loginAccessTokenTry) {
    return (
      <div className="f-c f-jcc f-aic f-dirc minWH100v">
        <h3 className="f2 f10 p-16">Trying to connect, this may take a few seconds ...</h3>
        <BarLoader height={4} width={100} color="#000000" loading={true} />
      </div>
    );
  }

  if (loginTry) {
    return (
      <div className="f-c f-jcc f-aic f-dirc minWH100v">
        <h3 className="f2 f10 p-16">Logging in ...</h3>
        <BarLoader height={4} width={100} color="#000000" loading={true} />
      </div>
    );
  }

  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />
      <Grid item xs={12} component={Paper} square>
        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            Unitpay
          </Typography>
          <form className={classes.form} noValidate>
            <TextField
              value={email.value}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              error={email.error}
              helperText={email.error ? 'Incorrect Email' : ''}
              onChange={handleEmail}
            />
            <TextField
              error={pass.error}
              value={pass.value}
              helperText={pass.error ? 'Incorrect Password' : ''}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              onChange={handlePassword}
            />

            <Button
              onClick={handleSubmit}
              type="submit"
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              Login
            </Button>
            {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
          </form>
        </div>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
